import { useNuxtApp } from '#app'
import { createVNode, getCurrentInstance, nextTick, render } from 'vue'
import type { AppContext, VNode } from 'vue'

// 计费规则浮层的命令式封装（参考 components/Account/useDomainError）。
// 单例挂载到 body，多处共用同一实例，调用方只需 useCostRule().open({...})，逻辑负担最小。

export type CostRuleOpenParams = {
  title?: string
  /** 已拼接好的文案行；空串表示空行（段落间距） */
  lines: string[]
  /** 触发的「计费规则」链接元素，浮层据其位置定位到正上方 */
  anchorEl?: HTMLElement | null
}

type CostRuleInstance = {
  open: (params: CostRuleOpenParams) => void
  close: () => void
}

let container: HTMLDivElement | null = null
let vnode: VNode | null = null
let instance: CostRuleInstance | null = null
let mountPromise: Promise<CostRuleInstance | null> | null = null

function ensureContainer() {
  if (!container) {
    container = document.createElement('div')
    container.setAttribute('data-cost-rule', '')
    document.body.appendChild(container)
  }
  return container
}

async function mountCostRule(appContext?: AppContext | null) {
  if (instance) {
    return instance
  }
  if (mountPromise) {
    return mountPromise
  }

  mountPromise = (async () => {
    const CostRuleDialog = (await import('./EnterpriseProtectAreaCostRule.vue')).default
    vnode = createVNode(CostRuleDialog)

    if (appContext) {
      vnode.appContext = appContext
    }

    render(vnode, ensureContainer())
    await nextTick()
    instance = vnode.component?.exposed as CostRuleInstance | null

    return instance
  })()

  return mountPromise
}

export function useCostRule() {
  const currentInstance = getCurrentInstance()
  const nuxtApp = useNuxtApp()
  const appContext = currentInstance?.appContext || nuxtApp.vueApp._context

  const open = async (params: CostRuleOpenParams) => {
    if (!import.meta.client) {
      return
    }
    const costRule = await mountCostRule(appContext)
    costRule?.open(params)
  }

  const close = () => {
    instance?.close()
  }

  return {
    open,
    close,
  }
}

export default useCostRule
